<template>
  <v-container fluid class="partner-finance pa-6">
    <v-row>
      <v-col cols="12">
        <div class="hero-card pa-6">
          <span class="eyebrow">Gestão financeira</span>
          <h1 class="text-h4 font-weight-bold mt-2 mb-2">
            Mensalidades dos alunos
          </h1>
          <p class="mb-0">
            Acompanhe todas as mensalidades das suas turmas e edite os valores
            que ainda não foram pagos.
          </p>
        </div>
      </v-col>
    </v-row>

    <v-row class="mt-4">
      <v-col cols="12">
        <v-alert
          type="info"
          color="secondary-500"
          border="left"
          colored-border
          dense
          outlined
          class="mb-0"
        >
          <strong>Como funciona a cobrança?</strong> A cobrança do mês é sempre
          referente às aulas do mês anterior. As cobranças são geradas
          automaticamente no dia 25 do mês anterior. O valor padrão é definido
          com base no plano de ensino selecionado pelo professor no módulo
          “Turma”, dentro do perfil do aluno. Caso seja necessário alterar o
          valor de uma mensalidade específica, a modificação deve ser feita até
          o último dia do mês anterior ao vencimento. Após esse prazo, a
          cobrança será gerada.
        </v-alert>
      </v-col>
    </v-row>

    <v-row class="mt-4">
      <v-col cols="12">
        <v-card outlined class="summary-card pa-4">
          <div
            class="d-flex flex-column flex-md-row align-md-center justify-space-between"
          >
            <div class="mb-3 mb-md-0">
              <h2 class="text-h6 mb-1">Dados de recebimento</h2>
              <p class="mb-0 muted-text">
                Cadastre sua conta bancária para receber os repasses das
                mensalidades dos seus alunos.
              </p>
            </div>
            <div class="d-flex align-center">
              <v-chip
                small
                :color="recipientReady ? '#2f855a' : '#b7791f'"
                dark
                class="mr-3"
              >
                {{ recipientReady ? "Conta ativa" : "Pendente" }}
              </v-chip>
              <v-btn
                color="secondary-500"
                dark
                depressed
                @click="openBankDialog"
              >
                {{ recipientReady ? "Editar conta" : "Cadastrar conta" }}
              </v-btn>
            </div>
          </div>
          <v-alert
            v-if="!recipientReady"
            type="warning"
            dense
            outlined
            border="left"
            class="mt-3 mb-0"
          >
            Sem uma conta bancária cadastrada você não recebe os repasses.
            Cadastre para ativar os pagamentos.
          </v-alert>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mt-2 align-end">
      <v-col cols="12" md="3">
        <v-select
          v-model="filters.month"
          :items="months"
          item-text="label"
          item-value="value"
          label="Mês"
          outlined
          dense
          hide-details
          @change="loadFinance"
        />
      </v-col>
      <v-col cols="6" md="2">
        <v-text-field
          v-model.number="filters.year"
          label="Ano"
          type="number"
          outlined
          dense
          hide-details
          @change="loadFinance"
        />
      </v-col>
      <v-col cols="6" md="4">
        <v-text-field
          v-model="filters.search"
          label="Buscar por aluno ou turma"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          clearable
          hide-details
        />
      </v-col>
      <v-col cols="12" md="3" class="d-flex justify-end">
        <v-btn
          color="secondary-500"
          dark
          depressed
          :loading="loading"
          @click="loadFinance"
        >
          Atualizar
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="mt-2">
      <v-col cols="12" md="4">
        <v-card outlined class="summary-card">
          <v-card-title class="pb-1">A receber</v-card-title>
          <v-card-text>
            <div class="text-h5 font-weight-bold">
              {{ formatCurrencyBR(summary.totalPendingAmount) }}
            </div>
            <div class="muted-text">
              {{ summary.pendingCount || 0 }} mensalidade(s) pendente(s)
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined class="summary-card">
          <v-card-title class="pb-1">Atrasadas</v-card-title>
          <v-card-text>
            <div class="text-h5 font-weight-bold">
              {{ formatCurrencyBR(summary.totalOverdueAmount) }}
            </div>
            <div class="muted-text">
              {{ summary.overdueCount || 0 }} vencida(s)
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined class="summary-card">
          <v-card-title class="pb-1">Pagas no período</v-card-title>
          <v-card-text>
            <div class="text-h5 font-weight-bold">
              {{ formatCurrencyBR(summary.totalPaidAmount) }}
            </div>
            <div class="muted-text">{{ summary.paidCount || 0 }} paga(s)</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card outlined class="charges-card">
          <v-data-table
            :headers="headers"
            :items="filteredItems"
            :loading="loading"
            item-key="id"
            no-data-text="Nenhuma mensalidade encontrada para o período."
          >
            <template v-slot:[`item.amount`]="{ item }">
              <div class="font-weight-bold">
                {{ formatCurrencyBR(item.amount) }}
              </div>
              <v-chip
                v-if="item.isManualAmount"
                x-small
                color="#e7f5f2"
                text-color="secondary-500"
                class="mt-1"
              >
                Ajustada
              </v-chip>
            </template>
            <template v-slot:[`item.status`]="{ item }">
              <v-chip small :color="chargeStatusColor(item.status)" dark>
                {{ chargeStatusLabel(item.status) }}
              </v-chip>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-tooltip bottom :disabled="canEditCharge(item)">
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <v-btn
                      small
                      text
                      color="secondary-500"
                      :disabled="!canEditCharge(item)"
                      @click="openAdjustmentDialog(item)"
                    >
                      Alterar
                    </v-btn>
                  </span>
                </template>
                <span>{{ disabledChargeReason(item) }}</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="adjustmentDialog.open" max-width="520">
      <v-card>
        <v-card-title>Alterar mensalidade</v-card-title>
        <v-card-text v-if="adjustmentDialog.charge">
          <div class="mb-4">
            <strong>{{ adjustmentDialog.charge.customerName }}</strong>
            <div class="muted-text">
              {{ adjustmentDialog.charge.classCode }} ·
              {{ adjustmentDialog.charge.referenceLabel }} ·
              {{ formatCurrencyBR(adjustmentDialog.charge.amount) }}
            </div>
          </div>

          <div class="section-label mb-2">Ajustar valor</div>
          <v-radio-group v-model="adjustmentDialog.mode" row class="mt-0">
            <v-radio label="Valor direto" value="FIXED" />
            <v-radio label="Percentual" value="PERCENTAGE" />
          </v-radio-group>
          <v-text-field
            v-if="adjustmentDialog.mode === 'FIXED'"
            v-model.number="adjustmentDialog.amount"
            label="Novo valor da mensalidade"
            prefix="R$"
            type="number"
            min="0.01"
            outlined
            dense
            hint="Informe o valor líquido que você recebe; a taxa da plataforma é somada automaticamente para o aluno."
            persistent-hint
          />
          <v-text-field
            v-else
            v-model.number="adjustmentDialog.percentage"
            label="Percentual de alteração"
            suffix="%"
            type="number"
            outlined
            dense
            hint="Use valores negativos para desconto, por exemplo -10."
            persistent-hint
          />

          <v-divider class="my-4" />

          <div class="section-label mb-3">Alterar status</div>
          <v-select
            v-model="adjustmentDialog.status"
            :items="statusOptions"
            item-text="text"
            item-value="value"
            label="Status da cobrança"
            outlined
            dense
            hide-details
          >
            <template v-slot:item="{ item }">
              <v-chip
                small
                :color="chargeStatusColor(item.value)"
                dark
                class="mr-2"
              >
                {{ item.text }}
              </v-chip>
            </template>
            <template v-slot:selection="{ item }">
              <v-chip small :color="chargeStatusColor(item.value)" dark>
                {{ item.text }}
              </v-chip>
            </template>
          </v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="adjustmentDialog.open = false">Cancelar</v-btn>
          <v-btn
            color="secondary-500"
            dark
            depressed
            :loading="adjustmentDialog.saving"
            @click="saveAdjustment"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="bankDialog.open" max-width="560" persistent>
      <v-card>
        <v-card-title>Dados de recebimento</v-card-title>
        <v-card-text>
          <v-form ref="bankForm">
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.trim="bankDialog.form.bankCode"
                  label="Código do banco"
                  hint="3 dígitos, ex: 341"
                  persistent-hint
                  maxlength="3"
                  :rules="[rules.required, rules.bankCode]"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="bankDialog.form.bankAccountType"
                  :items="accountTypeOptions"
                  item-text="text"
                  item-value="value"
                  label="Tipo de conta"
                  :rules="[rules.required]"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="8" md="9">
                <v-text-field
                  v-model.trim="bankDialog.form.bankBranchNumber"
                  label="Agência"
                  :rules="[rules.required]"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="4" md="3">
                <v-text-field
                  v-model.trim="bankDialog.form.bankBranchCheckDigit"
                  label="Dígito (opcional)"
                  maxlength="2"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="8" md="9">
                <v-text-field
                  v-model.trim="bankDialog.form.bankAccountNumber"
                  label="Conta"
                  :rules="[rules.required]"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="4" md="3">
                <v-text-field
                  v-model.trim="bankDialog.form.bankAccountCheckDigit"
                  label="Dígito"
                  maxlength="2"
                  :rules="[rules.required]"
                  outlined
                  dense
                />
              </v-col>
            </v-row>
            <v-alert type="info" dense text class="mb-0 mt-1">
              A conta deve estar no seu nome (mesmo CPF/CNPJ do cadastro).
            </v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="bankDialog.open = false">Cancelar</v-btn>
          <v-btn
            color="secondary-500"
            dark
            depressed
            :loading="bankDialog.saving"
            @click="saveBankAccount"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import partnerApi from "@/shared/pitchPartnerApi";

export default {
  name: "PartnerFinance",

  data() {
    const today = new Date();
    return {
      loading: false,
      filters: {
        month: today.getMonth() + 1,
        year: today.getFullYear(),
        search: "",
      },
      items: [],
      summary: {},
      months: [
        { value: 1, label: "Janeiro" },
        { value: 2, label: "Fevereiro" },
        { value: 3, label: "Março" },
        { value: 4, label: "Abril" },
        { value: 5, label: "Maio" },
        { value: 6, label: "Junho" },
        { value: 7, label: "Julho" },
        { value: 8, label: "Agosto" },
        { value: 9, label: "Setembro" },
        { value: 10, label: "Outubro" },
        { value: 11, label: "Novembro" },
        { value: 12, label: "Dezembro" },
      ],
      headers: [
        { text: "Aluno", value: "customerName" },
        { text: "Turma", value: "classCode" },
        { text: "Referência", value: "referenceLabel" },
        { text: "Vencimento", value: "dueDateLabel" },
        { text: "Status", value: "status" },
        { text: "Valor", value: "amount" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      adjustmentDialog: {
        open: false,
        saving: false,
        charge: null,
        mode: "FIXED",
        amount: null,
        percentage: null,
        status: null,
      },
      statusOptions: [
        { value: "PENDING", text: "Pendente" },
        { value: "OVERDUE", text: "Vencida" },
        { value: "PAID", text: "Paga" },
      ],
      recipient: { status: null, ready: false },
      bankDialog: {
        open: false,
        saving: false,
        form: {
          bankCode: "",
          bankBranchNumber: "",
          bankBranchCheckDigit: "",
          bankAccountNumber: "",
          bankAccountCheckDigit: "",
          bankAccountType: "checking",
        },
      },
      accountTypeOptions: [
        { value: "checking", text: "Conta corrente" },
        { value: "savings", text: "Conta poupança" },
      ],
      rules: {
        required: (v) =>
          (v !== null && v !== undefined && String(v).trim() !== "") ||
          "Campo obrigatório.",
        bankCode: (v) => /^\d{3}$/.test(v || "") || "Informe 3 dígitos.",
      },
      snackbar: { show: false, text: "", color: "success" },
    };
  },

  computed: {
    recipientReady() {
      return Boolean(this.recipient && this.recipient.ready);
    },
    filteredItems() {
      const term = (this.filters.search || "").trim().toLowerCase();
      if (!term) return this.items;
      return this.items.filter((item) =>
        [item.customerName, item.classCode]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(term))
      );
    },
  },

  mounted() {
    this.loadFinance();
  },

  methods: {
    async loadFinance() {
      this.loading = true;
      try {
        const response = await partnerApi.getPartnerFinance({
          month: this.filters.month,
          year: this.filters.year,
        });
        const data = response.data || {};
        this.items = data.items || [];
        this.summary = data.summary || {};
        this.recipient = data.recipient || { status: null, ready: false };
      } catch (error) {
        this.items = [];
        this.summary = {};
        this.showMessageFromError(
          error,
          "Não foi possível carregar as mensalidades."
        );
      } finally {
        this.loading = false;
      }
    },

    openBankDialog() {
      if (this.$refs.bankForm) this.$refs.bankForm.resetValidation();
      this.bankDialog.open = true;
    },

    async saveBankAccount() {
      if (this.$refs.bankForm && !this.$refs.bankForm.validate()) return;

      this.bankDialog.saving = true;
      try {
        const form = this.bankDialog.form;
        const payload = {
          bankCode: form.bankCode,
          bankBranchNumber: form.bankBranchNumber,
          bankAccountNumber: form.bankAccountNumber,
          bankAccountCheckDigit: form.bankAccountCheckDigit,
          bankAccountType: form.bankAccountType,
        };
        if (form.bankBranchCheckDigit) {
          payload.bankBranchCheckDigit = form.bankBranchCheckDigit;
        }

        const response = await partnerApi.updateBankAccount(payload);
        const result = response.data || {};
        this.recipient = {
          status: result.recipientStatus || null,
          ready: Boolean(result.gatewayRecipientId),
        };

        this.bankDialog.open = false;
        if (this.recipient.ready) {
          this.showMessage(
            "Conta cadastrada! Você já está apto a receber os repasses.",
            "success"
          );
        } else {
          this.showMessage(
            "Dados salvos, mas o cadastro do recebedor ainda não foi concluído. Verifique os dados e tente novamente.",
            "error"
          );
        }
      } catch (error) {
        this.showMessageFromError(
          error,
          "Não foi possível salvar os dados bancários."
        );
      } finally {
        this.bankDialog.saving = false;
      }
    },

    openAdjustmentDialog(charge) {
      this.adjustmentDialog = {
        open: true,
        saving: false,
        charge,
        mode: "FIXED",
        amount: charge.amount,
        percentage: null,
        status: charge.status,
      };
    },

    async saveAdjustment() {
      const { charge, mode, amount, percentage, status } =
        this.adjustmentDialog;
      if (!charge) return;

      const hasValueChange =
        mode === "FIXED"
          ? amount !== null && Number(amount) !== Number(charge.amount)
          : percentage !== null &&
            percentage !== undefined &&
            percentage !== "";

      const hasStatusChange = status && status !== charge.status;

      if (!hasValueChange && !hasStatusChange) {
        this.adjustmentDialog.open = false;
        return;
      }

      this.adjustmentDialog.saving = true;
      try {
        if (hasValueChange) {
          const payload =
            mode === "FIXED"
              ? { mode: "FIXED", amount: Number(amount) }
              : { mode: "PERCENTAGE", percentage: Number(percentage) };
          await partnerApi.updateGroupMonthlyCharge(
            charge.classId,
            charge.id,
            payload
          );
        }

        if (hasStatusChange) {
          await partnerApi.updateGroupChargeStatus(
            charge.classId,
            charge.id,
            status
          );
        }

        this.adjustmentDialog.open = false;
        await this.loadFinance();
        this.showMessage("Mensalidade atualizada com sucesso.", "success");
      } catch (error) {
        this.showMessageFromError(
          error,
          "Não foi possível salvar as alterações."
        );
      } finally {
        this.adjustmentDialog.saving = false;
      }
    },

    canEditCharge(item) {
      if (!item) return false;
      return item.status !== "PAID";
    },

    disabledChargeReason(item) {
      if (!item) return "";
      if (item.status === "PAID")
        return "Mensalidades pagas não podem ser alteradas.";
      return "";
    },

    chargeStatusLabel(status) {
      const labels = { PENDING: "Pendente", OVERDUE: "Vencida", PAID: "Paga" };
      return labels[status] || status;
    },

    chargeStatusColor(status) {
      const colors = {
        PENDING: "#b7791f",
        OVERDUE: "#c53030",
        PAID: "#2f855a",
      };
      return colors[status] || "#718096";
    },

    formatCurrencyBR(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },

    showMessage(text, color) {
      this.snackbar = { show: true, text, color };
    },

    showMessageFromError(error, fallback) {
      const message =
        error &&
        error.response &&
        error.response.data &&
        error.response.data.message
          ? error.response.data.message
          : fallback;
      this.showMessage(message, "error");
    },
  },
};
</script>

<style scoped>
.partner-finance {
  min-height: 100vh;
  background: radial-gradient(
      circle at top left,
      rgba(168, 38, 225, 0.14),
      transparent 32rem
    ),
    linear-gradient(180deg, #f6fbf8 0%, #ffffff 55%);
}

.hero-card {
  border-radius: 28px;
  background: linear-gradient(135deg, #a826e1 0%, #771ba0 100%);
  color: #fff;
  box-shadow: 0 22px 60px rgba(168, 38, 225, 0.18);
}

.eyebrow {
  color: #b9eadc;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.summary-card,
.charges-card {
  border-radius: 22px;
  border-color: rgba(168, 38, 225, 0.12) !important;
}

.muted-text {
  color: #6b7280;
}

.section-label {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
}
</style>
